import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { Search, RefreshCw, FileText, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';

import type { Json } from '@/integrations/supabase/types';

interface AdjustmentLog {
  id: string;
  correlation_id: string;
  adjustment_id: string;
  purchase_order_id: string;
  step_name: string;
  step_status: string; // 'started' | 'completed' | 'failed' | 'skipped'
  step_details: Json;
  error_message: string | null;
  execution_time_ms: number | null;
  created_at: string;
  created_by_user_id: string | null;
}

interface CorrelationGroup {
  correlationId: string;
  adjustmentId: string;
  purchaseOrderId: string;
  logs: AdjustmentLog[];
  status: 'success' | 'failed' | 'in_progress';
  startTime: string;
  totalDuration: number;
}

export default function AdjustmentLogsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [logs, setLogs] = useState<AdjustmentLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCorrelation, setSelectedCorrelation] = useState<string | null>(null);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('adjustment_processing_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch adjustment logs',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();

    // Set up realtime subscription
    const channel = supabase
      .channel('adjustment_logs')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'adjustment_processing_logs',
        },
        () => {
          fetchLogs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Group logs by correlation ID
  const groupedLogs = logs.reduce((acc, log) => {
    const existing = acc.find(g => g.correlationId === log.correlation_id);
    if (existing) {
      existing.logs.push(log);
    } else {
      acc.push({
        correlationId: log.correlation_id,
        adjustmentId: log.adjustment_id,
        purchaseOrderId: log.purchase_order_id,
        logs: [log],
        status: 'in_progress',
        startTime: log.created_at,
        totalDuration: 0,
      });
    }
    return acc;
  }, [] as CorrelationGroup[]);

  // Calculate status and duration for each group
  groupedLogs.forEach(group => {
    const hasFailure = group.logs.some(l => l.step_status === 'failed');
    const hasComplete = group.logs.some(l => l.step_name === 'workflow_complete');
    
    if (hasFailure) {
      group.status = 'failed';
    } else if (hasComplete) {
      group.status = 'success';
    }

    // Calculate total duration
    const durations = group.logs
      .filter(l => l.execution_time_ms !== null)
      .map(l => l.execution_time_ms as number);
    group.totalDuration = durations.reduce((sum, d) => sum + d, 0);
  });

  // Filter groups
  const filteredGroups = groupedLogs.filter(group => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      group.correlationId.toLowerCase().includes(term) ||
      group.adjustmentId.toLowerCase().includes(term) ||
      group.purchaseOrderId.toLowerCase().includes(term)
    );
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-amber-600" />;
    }
  };

  const getStepStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
      case 'success':
        return <Badge variant="default">Success</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'started':
      case 'in_progress':
        return <Badge variant="secondary">In Progress</Badge>;
      case 'skipped':
        return <Badge variant="outline">Skipped</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Change Order Processing Logs</h1>
          <p className="text-muted-foreground">
            Monitor and debug change order payment workflows
          </p>
        </div>
        <Button onClick={fetchLogs} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
          <CardDescription>
            Search by correlation ID, adjustment ID, or purchase order ID
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="details">Detailed View</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredGroups.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No adjustment logs found</p>
              </CardContent>
            </Card>
          ) : (
            filteredGroups.map(group => (
              <Card
                key={group.correlationId}
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => setSelectedCorrelation(
                  selectedCorrelation === group.correlationId ? null : group.correlationId
                )}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(group.status)}
                        <span className="font-mono text-sm">
                          {group.correlationId.substring(0, 8)}...
                        </span>
                        <Badge variant="outline">
                          {group.logs.length} steps
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Adjustment: {group.adjustmentId.substring(0, 8)}... | 
                        PO: {group.purchaseOrderId.substring(0, 8)}...
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {group.totalDuration}ms
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(group.startTime).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {selectedCorrelation === group.correlationId && (
                    <div className="mt-4 space-y-2 border-t pt-4">
                      {group.logs
                        .sort((a, b) => 
                          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                        )
                        .map(log => (
                          <div
                            key={log.id}
                            className="flex items-start justify-between p-3 bg-muted/50 rounded-md"
                          >
                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">
                                  {log.step_name}
                                </span>
                                {getStepStatusBadge(log.step_status)}
                              </div>
                              {log.error_message && (
                                <div className="flex items-start gap-2 text-sm text-red-600">
                                  <AlertCircle className="h-4 w-4 mt-0.5" />
                                  <span>{log.error_message}</span>
                                </div>
                              )}
                              {log.step_details && typeof log.step_details === 'object' && Object.keys(log.step_details as object).length > 0 && (
                                <details className="text-xs text-muted-foreground">
                                  <summary className="cursor-pointer">Details</summary>
                                  <pre className="mt-2 p-2 bg-background rounded overflow-x-auto">
                                    {JSON.stringify(log.step_details, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              {log.execution_time_ms && (
                                <p className="text-xs font-mono">
                                  {log.execution_time_ms}ms
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground">
                                {new Date(log.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="details" className="space-y-4">
          {loading ? (
            <Skeleton className="h-96 w-full" />
          ) : (
            <Card>
              <CardContent className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Timestamp</th>
                        <th className="text-left p-2">Correlation</th>
                        <th className="text-left p-2">Step</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-right p-2">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredGroups.flatMap(group =>
                        group.logs.map(log => (
                          <tr key={log.id} className="border-b hover:bg-muted/50">
                            <td className="p-2 font-mono text-xs">
                              {new Date(log.created_at).toLocaleString()}
                            </td>
                            <td className="p-2 font-mono text-xs">
                              {log.correlation_id.substring(0, 8)}...
                            </td>
                            <td className="p-2">{log.step_name}</td>
                            <td className="p-2">{getStepStatusBadge(log.step_status)}</td>
                            <td className="p-2 text-right font-mono text-xs">
                              {log.execution_time_ms ? `${log.execution_time_ms}ms` : '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
